// src/studio/api/studioApi.js
//
// The new Requirements & Design Studio's own API surface — independent of
// rds_ui/api/*.js on purpose, so rds_ui/ can be deleted in one commit at
// parity (see the studio redesign plan, Phase 5) without leaving the new
// studio importing from a folder that no longer exists.
import { api } from "../../api/client";

const BASE = "/rds";

// ── Projects ────────────────────────────────────────────────────────────

export const studioProjectsApi = {
  // Scoped to the engagement selected in the topbar (SPEC-07 D-5) — every
  // studio project belongs to exactly one.
  async list(engagementId) {
    const params = new URLSearchParams({ limit: "100" });
    if (engagementId) params.set("engagement_id", engagementId);
    const res = await api.get(`${BASE}/projects/?${params.toString()}`);
    return res.data;
  },

  async get(projectId) {
    const res = await api.get(`${BASE}/projects/${projectId}`);
    return res.data;
  },

  async create(data) {
    const res = await api.post(`${BASE}/projects/`, data);
    return res.data;
  },

  async remove(projectId) {
    await api.delete(`${BASE}/projects/${projectId}`);
  },
};

// ── Sources: SOW ────────────────────────────────────────────────────────

export const studioSowApi = {
  async uploadFile(projectId, file) {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post(`${BASE}/projects/${projectId}/sow/upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  // 204 (no SOW yet) resolves to null rather than throwing — "no SOW" is a
  // normal state for a project that hasn't reached Sources yet.
  async latest(projectId) {
    try {
      const res = await api.get(`${BASE}/projects/${projectId}/sow`);
      if (res.status === 204) return null;
      return res.data;
    } catch (err) {
      if (err?.response?.status === 204 || err?.response?.status === 404) return null;
      throw err;
    }
  },

  async parse(projectId, sowId) {
    const res = await api.post(`${BASE}/projects/${projectId}/sow/${sowId}/parse`);
    return res.data;
  },

  // Unlinks the SOW from this project (cascades to its sections/tables) so
  // a different file or Planning draft can be attached instead.
  async remove(projectId, sowId) {
    await api.delete(`${BASE}/projects/${projectId}/sow/${sowId}`);
  },
};

// ── Sources: the Planning-phase SOW seam (SPEC-07 §7) ────────────────────
// Whether the account has an active Planning (SOW analysis) subscription
// with an already-drafted SOW for this project's engagement, as an
// alternative to uploading one directly.

export const studioPlanningSowApi = {
  async availability(projectId) {
    const res = await api.get(`${BASE}/projects/${projectId}/planning-sow`);
    return res.data;
  },

  async import(projectId) {
    const res = await api.post(`${BASE}/projects/${projectId}/planning-sow/import`);
    return res.data;
  },
};

// ── Sources: interactions (meeting notes, email threads, transcripts) ────
// Re-exports src/api/interactionsApi.js rather than duplicating it — that
// file lives outside rds_ui/ already, so it survives the Phase 5 deletion
// untouched.
export { interactionsApi as studioInteractionsApi } from "../../api/interactionsApi";

// ── Config ──────────────────────────────────────────────────────────────

export const studioConfigApi = {
  async methodologies() {
    const res = await api.get(`${BASE}/config/methodologies`);
    return res.data;
  },
};

// ── Documents: the generic document-run model (SPEC-07 D-3) ─────────────

export const studioDocumentsApi = {
  // Every JadeDocumentType, and whether its section set is built yet.
  async types() {
    const res = await api.get(`${BASE}/documents`);
    return res.data;
  },

  // Section metadata for one document type — key, title, kind, columns,
  // guidance. Drives DocumentWorkspace's section rail and per-section
  // editor; a template gaining or renaming a section needs no frontend
  // change (SPEC-07 D-7).
  async sections(documentType) {
    const res = await api.get(`${BASE}/documents/${documentType}/sections`);
    return res.data;
  },

  // The ordered document chain for this project's engagement platform
  // (SPEC-07 D-2) — what the stage rail renders.
  async applicable(projectId) {
    const res = await api.get(`${BASE}/projects/${projectId}/documents/applicable`);
    return res.data;
  },

  // The most recent run of one document for a project, or null if that
  // document has never been generated.
  async latestRun(projectId, documentType) {
    const res = await api.get(
      `${BASE}/projects/${projectId}/documents/${documentType}/latest-run`
    );
    return res.data;
  },

  async startRun(projectId, documentType, { sowId, sourceRunId } = {}) {
    const res = await api.post(
      `${BASE}/projects/${projectId}/documents/${documentType}/generate`,
      { document_type: documentType, sow_id: sowId ?? null, source_run_id: sourceRunId ?? null }
    );
    return res.data;
  },

  async getRun(runId) {
    const res = await api.get(`${BASE}/documents/runs/${runId}`);
    return res.data;
  },

  // A Blob — the route requires the bearer token, so a plain <a href> can't
  // authenticate; same responseType: "blob" pattern as sowDraftApi.exportDocx
  // in src/api/client.js.
  async exportRun(runId) {
    const res = await api.get(`${BASE}/documents/runs/${runId}/export`, {
      responseType: "blob",
    });
    return res.data;
  },

  async exportPreview(runId) {
    const res = await api.get(`${BASE}/documents/runs/${runId}/preview`, {
      responseType: "blob",
    });
    return res.data;
  },

  // Manual save for one section's (or one GROUP item's) content — a straight
  // edit, or accepting an "Ask AI" suggestion once the user is satisfied
  // with it. groupKey is null for a flat section.
  async updateSection(runId, sectionKey, { contentMarkdown, groupKey } = {}) {
    const res = await api.patch(`${BASE}/documents/runs/${runId}/sections/${sectionKey}`, {
      content_markdown: contentMarkdown,
      group_key: groupKey ?? null,
    });
    return res.data;
  },

  // Asks the LLM to suggest a revision for one section/item — writes
  // nothing; the caller reviews the returned content_markdown and saves it
  // (unchanged or further edited) via updateSection.
  async previewSectionRevision(runId, sectionKey, { instruction, groupKey } = {}) {
    const res = await api.post(
      `${BASE}/documents/runs/${runId}/sections/${sectionKey}/preview-revision`,
      { instruction: instruction ?? null, group_key: groupKey ?? null }
    );
    return res.data;
  },
};
